/**
 * カバー写真の選択シート
 *
 * 旅の写真を日ごとのグリッドで並べ、タップしたものをカバーにする。
 */

import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Check, RotateCcw } from 'lucide-react'
import { useMemo } from 'react'

import type { ImmichAsset } from '@/api/services/immich'
import type { TripDay } from '@/api/types/trip'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ImmichThumbnail } from '@/pages/activity/components/activity-card/immich-thumbnail'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  days: TripDay[]
  coverAssetId: string | null
  hasCustomCover: boolean
  /** null は「自動（最初の写真）に戻す」 */
  onSelect: (assetId: string | null) => void
}

export const TripCoverSheet = ({ open, onOpenChange, days, coverAssetId, hasCustomCover, onSelect }: Props) => {
  const photoDays = useMemo(
    () =>
      days
        .map((day, i) => {
          const assets: ImmichAsset[] = [...day.visits.flatMap((v) => v.photos), ...day.photos]
          return {
            date: day.date,
            dayNumber: i + 1,
            images: assets.filter((a) => a.type === 'IMAGE'),
          }
        })
        .filter((day) => day.images.length > 0),
    [days],
  )

  const select = (assetId: string | null) => {
    onSelect(assetId)
    onOpenChange(false)
  }

  return (
    <BottomSheet title="カバー写真を選ぶ" open={open} onOpenChange={onOpenChange} snapPoints={[0.92]}>
      <div className="space-y-4 px-4 pb-8">
        {hasCustomCover && (
          <Button variant="outline" size="sm" className="w-full" onClick={() => select(null)}>
            <RotateCcw className="size-4" />
            自動（最初の写真）に戻す
          </Button>
        )}

        {photoDays.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">写真がありません</p>}

        {photoDays.map((day) => (
          <section key={day.date} className="space-y-1.5">
            <h3 className="text-xs font-semibold text-muted-foreground">
              DAY {day.dayNumber} ・ {format(new Date(`${day.date}T00:00:00`), 'M月d日(E)', { locale: ja })}
            </h3>
            <div className="grid grid-cols-3 gap-1">
              {day.images.map((asset) => {
                const isCover = asset.id === coverAssetId
                return (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => select(asset.id)}
                    aria-label={isCover ? '現在のカバー写真' : 'この写真をカバーにする'}
                    aria-pressed={isCover}
                    className={cn(
                      'relative aspect-square overflow-hidden rounded-md',
                      isCover && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
                    )}
                  >
                    <ImmichThumbnail
                      assetId={asset.id}
                      alt={asset.originalFileName}
                      size="thumbnail"
                      className="h-full w-full"
                    />
                    {isCover && (
                      <span className="absolute top-1 right-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                        <Check className="size-3.5" />
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </section>
        ))}
      </div>
    </BottomSheet>
  )
}
